// Mapbox helpers shared across the web app (geocoding, reverse geocoding,
// place search, directions). All functions take the public access token
// (pk....) explicitly so callers can source it from ConfigurableValues().
// Uses the native fetch API (no extra dependency).

const GEOCODE_BASE = "https://api.mapbox.com/geocoding/v5/mapbox.places";
const DIRECTIONS_BASE = "https://api.mapbox.com/directions/v5/mapbox/driving";

// ISO 3166-1 alpha-2, comma separated. Scopes every place search to where we
// actually deliver.
const DEFAULT_COUNTRY = "us";

// Haversine distance in meters between two {lat,lng} points.
export function distanceMeters(a, b) {
  if (!a || !b) return Infinity;
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

function buildQuery(params) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null) qs.append(k, v);
  });
  return qs.toString();
}

async function getJson(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Mapbox request failed: ${res.status}`);
  return res.json();
}

// Reverse geocode: coordinates -> { formattedAddress, city }
export async function reverseGeocode(latitude, longitude, token) {
  if (!token) throw new Error("Mapbox token missing");
  const qs = buildQuery({ access_token: token, language: "en", limit: 1 });
  const data = await getJson(`${GEOCODE_BASE}/${longitude},${latitude}.json?${qs}`);
  const feature = data?.features?.[0];
  if (!feature) throw new Error("No address found for the given coordinates.");
  const formattedAddress = feature.place_name;
  // city = the "place" context entry (locality fallback)
  const cityCtx =
    feature.context?.find((c) => c.id.startsWith("place")) ||
    feature.context?.find((c) => c.id.startsWith("locality"));
  const city = cityCtx ? cityCtx.text : null;
  return { formattedAddress, city };
}

// Forward search: query string -> array of suggestions.
// Each suggestion: { id, name, placeName, lat, lng, bbox }
// Results are limited to the United States by default — we only operate there,
// so worldwide matches are noise. Pass options.country to widen or change it.
// Nearby results come first: pass options.proximity ("lng,lat"), otherwise
// Mapbox biases by the visitor's IP. Without either it ranks the whole country
// by text alone, so "15 wright st" listed Indiana and Ohio before Stoneham, MA.
export async function searchPlaces(query, token, options = {}) {
  if (!token || !query) return [];
  const qs = buildQuery({
    access_token: token,
    language: "en",
    limit: options.limit || 5,
    types: options.types,
    proximity: options.proximity || "ip",
    country: options.country || DEFAULT_COUNTRY,
  });
  const data = await getJson(
    `${GEOCODE_BASE}/${encodeURIComponent(query)}.json?${qs}`
  );
  return (data?.features || []).map((f) => ({
    id: f.id,
    name: f.text,
    placeName: f.place_name,
    lng: f.center[0],
    lat: f.center[1],
    bbox: f.bbox || null, // [minLng, minLat, maxLng, maxLat]
  }));
}

// Street addresses for an address form, split into the parts the form has.
// Each: { id, label, address1, city, state, zip, lat, lng }; state is the
// two-letter code ("MA"). US only unless options.country says otherwise.
export async function searchAddresses(query, token, options = {}) {
  if (!token || !query) return [];
  const qs = buildQuery({
    access_token: token,
    language: "en",
    limit: options.limit || 5,
    types: "address",
    autocomplete: "true",
    country: options.country || DEFAULT_COUNTRY,
    // Nearby addresses first: Mapbox biases by the visitor's IP location.
    proximity: options.proximity || "ip",
  });
  const data = await getJson(`${GEOCODE_BASE}/${encodeURIComponent(query)}.json?${qs}`);
  return (data?.features || []).map((f) => {
    const ctx = (prefix) => (f.context || []).find((c) => c.id.startsWith(prefix));
    const region = ctx("region");
    const place = ctx("place") || ctx("locality");
    const postcode = ctx("postcode");
    return {
      id: f.id,
      label: f.place_name,
      // f.address is the house number; f.text the street.
      address1: f.address ? `${f.address} ${f.text}` : f.text,
      city: place ? place.text : "",
      state: region && region.short_code ? region.short_code.replace(/^[A-Z]{2}-/i, "").toUpperCase() : "",
      zip: postcode ? postcode.text : "",
      lng: f.center[0],
      lat: f.center[1],
    };
  });
}

// Driving directions between two {lat,lng} points.
// Returns { geojson, distance, duration } where geojson is a LineString Feature.
export async function getDirections(origin, dest, token) {
  if (!token || !origin || !dest) return null;
  const coords = `${origin.lng},${origin.lat};${dest.lng},${dest.lat}`;
  const qs = buildQuery({
    access_token: token,
    geometries: "geojson",
    overview: "full",
  });
  const data = await getJson(`${DIRECTIONS_BASE}/${coords}?${qs}`);
  const route = data?.routes?.[0];
  if (!route) return null;
  return {
    geojson: { type: "Feature", properties: {}, geometry: route.geometry },
    distance: route.distance,
    duration: route.duration,
  };
}
